/**
 * 소셜 로그인 추상화 계층 (Google / Kakao / Naver)
 *
 * 목표: UI·세션 배선을 지금 깔아두고, 키/플러그인만 붙으면 실제 동작하도록 분기.
 *
 * 현재 상태
 *  - Google: 웹=signInWithPopup(즉시 동작, Firebase 설정 시). 네이티브=Capacitor Firebase
 *    Authentication 플러그인 경유(미설치 시 AuthNotConfiguredError).
 *  - Kakao / Naver: Firebase 기본 제공자가 아님 → 각 네이티브/JS SDK로 토큰 취득 후
 *    Cloud Function으로 Firebase 커스텀 토큰 발급 → signInWithCustomToken.
 *    (백엔드 함수·앱 키 준비 전까지 AuthNotConfiguredError로 "준비 중" 노출)
 *
 * 붙이는 순서(키 확보 시)
 *  1) Firebase 콘솔에 Android 앱 등록 + SHA-1 + google-services.json → android/app/
 *  2) npm i @capacitor-firebase/authentication  (Google 네이티브)
 *  3) Kakao/Naver: 네이티브 SDK + functions/ 커스텀 토큰 엔드포인트 → 아래 stub 교체
 */

import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { isNative } from './native'
import type { User, UILanguage } from '@/types'

export type SocialProvider = 'google' | 'kakao' | 'naver'

/** 아직 키·플러그인·백엔드가 준비되지 않은 제공자 — UI에서 "준비 중"으로 처리 */
export class AuthNotConfiguredError extends Error {
  provider: SocialProvider
  constructor(provider: SocialProvider) {
    super(`${provider} sign-in is not configured yet`)
    this.name = 'AuthNotConfiguredError'
    this.provider = provider
  }
}

/** Firebase 사용자 → 앱 User(표시용 최소 매핑, 학습 데이터는 learnerProfileStore가 소스) */
type FbUserLike = { uid: string; email: string | null; displayName: string | null; photoURL: string | null }
function mapUser(fb: FbUserLike): User {
  return {
    id: fb.uid,
    email: fb.email ?? '',
    displayName: fb.displayName ?? fb.email?.split('@')[0] ?? 'Learner',
    photoURL: fb.photoURL ?? undefined,
    uiLanguage: 'en' as UILanguage,
    nativeLanguage: 'en',
    learningLanguages: [],
    progress: {} as User['progress'],
    totalXp: 0,
    level: 1,
    streak: { current: 0, longest: 0, lastStudyDate: '' },
    badges: [],
    dailyGoal: 10,
    soundEnabled: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  }
}

/** 스토어 심사용 리뷰어 계정 — 로그인 시 프리미엄 전 기능 자동 해제(실결제 없이 검수).
 *  Google이 검증한 이메일 기준이라, 해당 계정(비밀번호 보유)으로 로그인해야만 적용된다. */
const REVIEWER_EMAILS = ['speakflow.review@gmail.com']
const REVIEWER_PREMIUM_UNTIL = 4102444800000 // 2100-01-01 (사실상 무기한)

/** Firebase 세션 → useAuthStore 미러링(로그인 상태를 앱 본체가 소비하도록). main에서 1회 호출. */
export async function initAuthSession(): Promise<void> {
  const { auth, isFirebaseConfigured } = await import('./firebase')
  if (!isFirebaseConfigured || !auth) return
  const { onAuthStateChanged } = await import('firebase/auth')
  const { useAuthStore } = await import('@/stores/useStore')
  onAuthStateChanged(auth, async (fb) => {
    useAuthStore.getState().setUser(fb ? mapUser(fb) : null)
    if (fb?.email && REVIEWER_EMAILS.includes(fb.email.toLowerCase())) {
      const { useLearnerProfileStore } = await import('@/stores/learnerProfileStore')
      useLearnerProfileStore.getState().setPremiumUntil(REVIEWER_PREMIUM_UNTIL)
    }
  })
}

async function signInGoogle(): Promise<void> {
  const { auth, googleProvider, isFirebaseConfigured } = await import('./firebase')
  if (!isFirebaseConfigured || !auth || !googleProvider) throw new AuthNotConfiguredError('google')

  if (isNative()) {
    // 네이티브: 구글이 임베디드 웹뷰 OAuth를 차단하므로 네이티브 SDK 경유.
    // 플러그인은 정적 import로 번들에 포함한다 — 과거 동적 import(/* @vite-ignore */ pkg)는
    // webview에서 bare 스펙을 URL로 해석하려다 항상 실패해 "준비 중"으로 오표시되는 버그였음.
    // 네이티브 자체 로그인 실패는 그대로 전파해 UI가 "로그인 실패"로 안내하도록 한다.
    const res = await FirebaseAuthentication.signInWithGoogle()
    const idToken: string | undefined = res?.credential?.idToken
    if (!idToken) throw new Error('Google sign-in returned no idToken')
    const { GoogleAuthProvider, signInWithCredential } = await import('firebase/auth')
    await signInWithCredential(auth, GoogleAuthProvider.credential(idToken))
    return
  }

  const { signInWithPopup } = await import('firebase/auth')
  await signInWithPopup(auth, googleProvider)
}

async function signInKakao(): Promise<void> {
  // TODO(키 확보 시): Kakao SDK로 로그인 → accessToken → functions(kakaoLogin)에서
  // Firebase 커스텀 토큰 발급 → signInWithCustomToken(auth, token).
  throw new AuthNotConfiguredError('kakao')
}

async function signInNaver(): Promise<void> {
  // TODO(키 확보 시): Naver SDK로 로그인 → accessToken → functions(naverLogin)에서
  // Firebase 커스텀 토큰 발급 → signInWithCustomToken(auth, token).
  throw new AuthNotConfiguredError('naver')
}

/** 소셜 로그인 진입점. 성공 시 onAuthStateChanged가 세션을 반영한다. */
export async function signIn(provider: SocialProvider): Promise<void> {
  if (provider === 'google') return signInGoogle()
  if (provider === 'kakao') return signInKakao()
  return signInNaver()
}

export async function signOutUser(): Promise<void> {
  const { auth } = await import('./firebase')
  if (auth) {
    const { signOut } = await import('firebase/auth')
    await signOut(auth)
  }
  const { useAuthStore } = await import('@/stores/useStore')
  useAuthStore.getState().logout()
}
